import {useEffect, useLayoutEffect, useRef, useState} from 'react';

const HEIGHT = 280;
const AXIS_Y = 130;
const PAD = 70;

const LEVELS = [
    {up: true,  armLen: 55},
    {up: false, armLen: 55},
    {up: true,  armLen: 90},
    {up: false, armLen: 90},
];

const MilestoneChart = ({milestones, hoveredId, onHover}) => {
    const wrapRef = useRef(null);
    const [width, setWidth] = useState(600);
    const [tooltipPos, setTooltipPos] = useState(null);

    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const update = () => setWidth(el.offsetWidth);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    useLayoutEffect(() => {
        const el = wrapRef.current;
        if (el) el.scrollLeft = el.scrollWidth;
    }, [milestones]);

    const svgWidth = Math.max(width, milestones.length * 110);
    const timestamps = milestones.map(m => new Date(m.milestoneDate).getTime());
    const minT = Math.min(...timestamps);
    const maxT = Math.max(...timestamps);
    const span = maxT - minT || 1;

    const xOfT = t => PAD + ((t - minT) / span) * (svgWidth - 2 * PAD);
    const xOf  = m => xOfT(new Date(m.milestoneDate).getTime());

    // Year dividers: one vertical line per Jan-1 boundary within the range
    const minYear = new Date(minT).getFullYear();
    const maxYear = new Date(maxT).getFullYear();

    const yearDividers = [];
    for (let y = minYear + 1; y <= maxYear; y++) {
        yearDividers.push({year: y, x: xOfT(new Date(y, 0, 1).getTime())});
    }

    // Year labels: one per year, centered over its visible slice
    const yearLabels = [];
    for (let y = minYear; y <= maxYear; y++) {
        const sliceStart = Math.max(minT, new Date(y, 0, 1).getTime());
        const sliceEnd   = Math.min(maxT, new Date(y + 1, 0, 1).getTime());
        yearLabels.push({year: y, x: xOfT((sliceStart + sliceEnd) / 2)});
    }

    return (
        <div className="milestone-chart" ref={wrapRef}>
            <svg width={svgWidth} height={HEIGHT} className="milestone-chart__svg">
                <defs>
                    <marker id="mc-arrow" markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto">
                        <path d="M0,0 L0,6 L8,3 z" className="milestone-chart__arrowhead"/>
                    </marker>
                </defs>

                {/* Year dividers — rendered first so they sit behind everything */}
                {yearDividers.map(({year, x}) => (
                    <line
                        key={`ydiv-${year}`}
                        x1={x} y1={0} x2={x} y2={HEIGHT - 24}
                        className="milestone-chart__year-div"
                    />
                ))}

                {/* Year labels at the bottom */}
                {yearLabels.map(({year, x}) => (
                    <text
                        key={`ylbl-${year}`}
                        x={x} y={HEIGHT - 10}
                        textAnchor="middle"
                        className="milestone-chart__year-label"
                    >
                        {year}
                    </text>
                ))}

                {/* Axis */}
                <line
                    x1={PAD / 2} y1={AXIS_Y}
                    x2={svgWidth - PAD / 4} y2={AXIS_Y}
                    className="milestone-chart__axis"
                    markerEnd="url(#mc-arrow)"
                />

                {/* Milestones */}
                {milestones.map((m, i) => {
                    const {up, armLen} = LEVELS[i % LEVELS.length];
                    const x = xOf(m);
                    const armEndY = up ? AXIS_Y - armLen : AXIS_Y + armLen;
                    const titleY  = up ? armEndY - 16 : armEndY + 14;
                    const dateY   = up ? armEndY - 3  : armEndY + 27;
                    const label   = m.title.length > 18 ? m.title.slice(0, 17) + '…' : m.title;
                    const active  = m.id === hoveredId;

                    return (
                        <g
                            key={m.id}
                            className={`milestone-chart__item${active ? ' milestone-chart__item--active' : ''}`}
                            onMouseEnter={() => onHover(m.id)}
                            onMouseLeave={() => { onHover(null); setTooltipPos(null); }}
                            onMouseMove={e => setTooltipPos({m, x: e.clientX, y: e.clientY})}
                        >
                            <line x1={x} y1={up ? AXIS_Y - 5 : AXIS_Y + 5} x2={x} y2={armEndY} className="milestone-chart__arm"/>
                            <circle cx={x} cy={AXIS_Y} r={5} className="milestone-chart__dot"/>
                            <text x={x} y={titleY} textAnchor="middle" className="milestone-chart__label">{label}</text>
                            <text x={x} y={dateY}  textAnchor="middle" className="milestone-chart__date">{m.milestoneDate}</text>
                        </g>
                    );
                })}
            </svg>

            {tooltipPos && (() => {
                const vw = window.innerWidth;
                const vh = window.innerHeight;
                const flipX = tooltipPos.x + 14 + 240 > vw;
                const flipY = tooltipPos.y + 14 + 120 > vh;
                return (
                <div
                    className="milestone-chart__tooltip"
                    style={{
                        left:   flipX ? 'auto' : tooltipPos.x + 14,
                        right:  flipX ? vw - tooltipPos.x + 14 : 'auto',
                        top:    flipY ? 'auto' : tooltipPos.y + 14,
                        bottom: flipY ? vh - tooltipPos.y + 14 : 'auto',
                    }}
                >
                    <div className="milestone-chart__tooltip-title">{tooltipPos.m.title}</div>
                    <div className="milestone-chart__tooltip-date">{tooltipPos.m.milestoneDate}</div>
                    {tooltipPos.m.description && (
                        <div className="milestone-chart__tooltip-desc">{tooltipPos.m.description}</div>
                    )}
                </div>
                );
            })()}
        </div>
    );
};

export default MilestoneChart;